/**
 * Guest CTA row (CU-868k59z4w): keeps the card sized to the scroll area and re-anchored after
 * the Nth visible player row.
 *
 * Calls on sibling mixins via `this`: Filtering (playerSearchTerm).
 */

export default () => ({
    _guestCtaWidthRaf: null,

    // Re-query each time so htmx swaps of the table never leave a stale reference.
    getGuestCtaEl() {
        return this.$refs.rankingsTable?.querySelector('tbody[data-cta-row]') ?? null;
    },

    // Keep the guest CTA card sized to the visible width of the horizontal scroll area.
    // Also mirror the scroller's scroll-padding-left (negated) - scroll-padding insets
    // the sticky constraint rectangle, which would otherwise shift the card right by
    // the pinned columns' width whenever the CTA's td is wider than the card.
    syncGuestCtaViewportWidth(cta = this.getGuestCtaEl()) {
        if (!cta) {
            return;
        }
        const scroller = document.querySelector('.main-ranking-div');
        if (scroller) {
            cta.style.setProperty('--rankings-guest-cta-viewport', `${scroller.clientWidth}px`);
            const scrollPaddingLeft = parseFloat(getComputedStyle(scroller).scrollPaddingLeft) || 0;
            cta.style.setProperty('--rankings-guest-cta-left', `${-scrollPaddingLeft}px`);
        }
    },

    // Re-anchor the guest CTA after the Nth visible player row (N = data-cta-after-row,
    // set server-side in _rankings-table-rows.php). Hidden while a player-name search is
    // active; otherwise always anchored, clamped to the last visible row.
    repositionGuestCta() {
        const cta = this.getGuestCtaEl();
        if (!cta) {
            return; // logged-in: element never rendered
        }

        const searching = this.playerSearchTerm && this.playerSearchTerm.length >= 3;
        cta.classList.toggle('search-hidden', !!searching);
        if (searching) {
            return;
        }

        const afterN = parseInt(cta.dataset.ctaAfterRow, 10) || 4;
        const visibleRows = Array.from(this.$refs.rankingsTable.querySelectorAll('tbody[data-player-row]')).filter(
            el => el.style.display !== 'none' && !el.classList.contains('hidden-row') && !el.classList.contains('search-hidden')
        );
        if (!visibleRows.length) {
            cta.classList.add('search-hidden');
            return;
        }
        const anchor = visibleRows[Math.min(afterN, visibleRows.length) - 1];
        if (anchor.nextElementSibling !== cta) {
            // tbody-after-tbody is valid table content; skipping no-op moves keeps a playing video alive
            anchor.after(cta);
        }
        // Deferred: the width sync reads scroller geometry, which forces layout of the
        // just-rebuilt table if done inside the click handler. One frame later the same
        // read is free, and the card's custom properties rarely change anyway.
        if (this._guestCtaWidthRaf) {
            cancelAnimationFrame(this._guestCtaWidthRaf);
        }
        this._guestCtaWidthRaf = requestAnimationFrame(() => {
            this._guestCtaWidthRaf = null;
            this.syncGuestCtaViewportWidth();
        });
    },
});
